import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  BENEFICIARY_NO_BENEFICIARY,
  BeneficiaryStatus
} from '@percent/admin-dashboard/api/actions/disbursements/disbursements.types'
import { DisbursementsBreakdownQueryParams } from './DisbursementsBreakdownFilters.types'

type UseBeneficiaryStatusFilterProps = {
  queryParams: DisbursementsBreakdownQueryParams
  setQueryParams: (queryParams: DisbursementsBreakdownQueryParams) => void
}

const NEW_FILTER_VALUE = 'NEW' as const

const newFilterApiValues = [BeneficiaryStatus.PROCESSING, BENEFICIARY_NO_BENEFICIARY] as const

export const useBeneficiaryStatusFilter = ({ queryParams, setQueryParams }: UseBeneficiaryStatusFilterProps) => {
  const { t } = useTranslation()

  const beneficiaryStatusesOptions = useMemo(
    () => [
      { label: t('status.beneficiaryConfirmed'), value: BeneficiaryStatus.ACTIVE },
      { label: t('status.beneficiaryNew'), value: NEW_FILTER_VALUE },
      { label: t('status.beneficiaryUpdated'), value: BeneficiaryStatus.OUTDATED }
    ],
    [t]
  )

  const beneficiaryFilterLabelsByValue = useMemo(
    () =>
      beneficiaryStatusesOptions.reduce<Record<string, string>>(
        (acc, { value, label }) => ({
          ...acc,
          [value]: label,
          ...(value === NEW_FILTER_VALUE
            ? Object.fromEntries(newFilterApiValues.map(apiValue => [apiValue, label]))
            : {})
        }),
        {}
      ),
    [beneficiaryStatusesOptions]
  )

  const selectedUiValues = useMemo(() => {
    const apiValues = queryParams?.beneficiaryStatus ?? []

    return [
      ...(apiValues.includes(BeneficiaryStatus.ACTIVE) ? [BeneficiaryStatus.ACTIVE] : []),
      ...(apiValues.includes(BeneficiaryStatus.OUTDATED) ? [BeneficiaryStatus.OUTDATED] : []),
      ...(apiValues.some(value => newFilterApiValues.includes(value as typeof newFilterApiValues[number]))
        ? [NEW_FILTER_VALUE]
        : [])
    ]
  }, [queryParams?.beneficiaryStatus])

  const onBeneficiaryStatusChange = useCallback(
    (uiStatuses: string[]) => {
      const apiStatuses = uiStatuses.flatMap(status =>
        status === NEW_FILTER_VALUE ? [...newFilterApiValues] : [status]
      )

      setQueryParams({ ...queryParams, beneficiaryStatus: apiStatuses })
    },
    [queryParams, setQueryParams]
  )

  return {
    beneficiaryStatusesOptions,
    beneficiaryFilterLabelsByValue,
    onBeneficiaryStatusChange,
    selectedBeneficiaryStatuses: selectedUiValues
  }
}
